import { useEffect, useState } from 'react';
import { BarChart3, Eye, Loader2, TrendingUp, Users } from 'lucide-react';
import { apiFetch } from '../../utils/operations';

const StatCard = ({ label, value, testid }) => (
  <div className="eco-admin-card p-5" data-testid={testid}>
    <p className="text-[13px] font-medium text-eco-ink/55">{label}</p>
    <p className="eco-number mt-2 text-[28px] leading-tight font-semibold tracking-[-0.02em] text-eco-green">{value}</p>
  </div>
);

const TopTable = ({ title, rows, testid }) => (
  <div className="rounded-xl border border-eco-ink/10 bg-white p-6" data-testid={testid}>
    <h3 className="text-base font-semibold text-eco-ink">{title}</h3>
    <div className="mt-4 space-y-2">
      {rows.map((r, i) => (
        <p key={r.slug} data-testid={`${testid}-row-${r.slug.replaceAll('/', '-')}`} className="flex items-baseline justify-between gap-3 text-sm">
          <span className="min-w-0 truncate text-eco-ink/65"><span className="mr-2 text-xs text-eco-ink/35">{i + 1}.</span>{r.name || r.slug}</span>
          <strong className="shrink-0 text-eco-ink">{r.views}</strong>
        </p>
      ))}
      {rows.length === 0 && <p className="text-sm text-eco-ink/45">Sem visualizações registadas ainda.</p>}
    </div>
  </div>
);

export const AdminContentAnalytics = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiFetch('/admin/analytics/content').then(setData).catch(() => setFailed(true));
  }, []);

  if (failed) return <p className="text-sm text-eco-ink/50" data-testid="content-analytics-error">Não foi possível carregar as estatísticas de conteúdo.</p>;
  if (!data) return <p className="flex items-center gap-2 text-sm text-eco-ink/50" data-testid="content-analytics-loading"><Loader2 size={15} className="animate-spin" /> A carregar estatísticas de conteúdo…</p>;

  const maxSeries = Math.max(1, ...data.series_30d.map((d) => d.views));
  const rate = data.conversion_rate_30d != null ? `${(data.conversion_rate_30d * 100).toFixed(1)}%` : '—';

  return (
    <div data-testid="admin-content-analytics" className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-eco-ink"><Eye size={18} className="text-eco-gold" /> Visualizações de conteúdo público</h2>
        <p className="mt-1 text-xs text-eco-ink/45">Anónimo e sem dados pessoais (sem IP nem user-agent). Refreshes do mesmo visitante no mesmo dia contam uma vez.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Visualizações totais" value={data.total_views} testid="ca-total" />
        <StatCard label="Hoje" value={data.views_today} testid="ca-today" />
        <StatCard label="Últimos 7 dias" value={data.views_7d} testid="ca-7d" />
        <StatCard label="Últimos 30 dias" value={data.views_30d} testid="ca-30d" />
        <StatCard label="Visitantes únicos (30d)" value={data.unique_visitors_30d} testid="ca-unique" />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="eco-admin-card p-5" data-testid="ca-requests">
          <p className="flex items-center gap-2 text-[13px] font-medium text-eco-ink/55"><Users size={14} /> Pedidos (30d)</p>
          <p className="eco-number mt-2 text-[28px] font-semibold text-eco-green">{data.requests_30d}</p>
          <p className="mt-1 text-xs text-eco-ink/45">Total histórico: {data.requests_total}</p>
        </div>
        <div className="eco-admin-card p-5" data-testid="ca-proposals">
          <p className="flex items-center gap-2 text-[13px] font-medium text-eco-ink/55"><BarChart3 size={14} /> Propostas aceites (30d)</p>
          <p className="eco-number mt-2 text-[28px] font-semibold text-eco-green">{data.proposals_accepted_30d}</p>
        </div>
        <div className="eco-admin-card p-5" data-testid="ca-conversion">
          <p className="flex items-center gap-2 text-[13px] font-medium text-eco-ink/55"><TrendingUp size={14} /> Conversão views → pedidos (30d)</p>
          <p className="eco-number mt-2 text-[28px] font-semibold text-eco-green">{rate}</p>
        </div>
      </div>

      <div className="rounded-xl border border-eco-ink/10 bg-white p-6" data-testid="ca-series">
        <h3 className="text-base font-semibold text-eco-ink">Visualizações por dia (30 dias)</h3>
        <div className="mt-4 flex h-24 items-end gap-[3px]">
          {data.series_30d.map((d) => (
            <div key={d.day} title={`${d.day}: ${d.views}`} data-testid={`ca-bar-${d.day}`} className="flex-1 rounded-t bg-eco-green/70" style={{ height: `${Math.max(3, (d.views / maxSeries) * 100)}%` }} />
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-eco-ink/40">
          <span>{data.series_30d[0]?.day}</span><span>{data.series_30d[29]?.day}</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <TopTable title="Experiências mais vistas (30d)" rows={data.top_experiences} testid="ca-top-experiences" />
        <TopTable title="Viagens Completas mais vistas (30d)" rows={data.top_packages} testid="ca-top-packages" />
        <TopTable title="Destinos mais vistos (30d)" rows={data.top_destinations} testid="ca-top-destinations" />
        <TopTable title="Alojamentos mais vistos (30d)" rows={data.top_accommodations} testid="ca-top-accommodations" />
      </div>
    </div>
  );
};
